import { AspectRatio, Button, Flex, Image, Text, VStack, useDisclosure } from "@chakra-ui/react";
import { useRouter } from "next/router";
import type { ResourceCoreDataFragment } from "~/__generated__/graphql";
import useNotify from "~/hooks/use-notify";
import { LargeBreak, MediumBreak, SmallBreak } from "../landing/v2/helpers";
import PopUp from "./pop-up";

export type ResourceProps = {
  data: {
    resources: ResourceCoreDataFragment[];
  };
};
type CardProps = {
  dataResource: ResourceCoreDataFragment;
};
const Card = ({ dataResource }: CardProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const router = useRouter();
  const notify = useNotify();
  return (
    <Flex
      width="full"
      justify={"space-between"}
      fontFamily={"'Inter', sans-serif"}
      mx="auto"
      gap={{ xl: "32px", lg: "24px", base: "24px" }}
      direction={{ lg: "row", base: "column" }}
    >
      <AspectRatio
        position="relative"
        width={{ xl: "674px", lg: "570px", base: "full" }}
        height={{ xl: "379px", lg: "311px", base: "193px" }}
      >
        <Image
          src={dataResource?.thumbnail?.data?.attributes?.url}
          alt=""
          objectFit={"contain"}
          height="full"
          w="full"
        />
      </AspectRatio>

      <VStack spacing="12px" align={"start"} w={{ xl: "414px", lg: "382px" }} justify={"center"}>
        <Text
          fontSize={{ xl: "18px", lg: "14px", base: "16px" }}
          lineHeight={{ xl: "18px", lg: "14px", base: "24px" }}
          letterSpacing={{ xl: "unset", lg: "0.02em" }}
          color="rgba(13, 48, 80, 0.8)"
        >
          {dataResource.category}
        </Text>
        <VStack spacing="8px" align={"start"}>
          <Text
            noOfLines={3}
            color="#0D3050"
            fontSize={{ xl: "32px", lg: "24px", base: "24px" }}
            lineHeight={{ xl: "44px", lg: "24px", base: "32px" }}
            fontWeight={600}
            dangerouslySetInnerHTML={{ __html: dataResource.title ?? "" }}
          />

          <Text
            fontSize={{ lg: "16px", base: "14px" }}
            lineHeight={{ lg: "24px", base: "20px" }}
            color="rgba(13, 48, 80, 0.8)"
            letterSpacing={"0.02em"}
            dangerouslySetInnerHTML={{ __html: dataResource.description ?? "" }}
          />
        </VStack>
        <Text
          color="rgba(13, 48, 80, 0.8)"
          fontSize={{ lg: "56px", base: "44px" }}
          lineHeight={{ lg: "64px", base: "48px" }}
          pb={{ lg: "12px", base: "8px" }}
          fontFamily={"Georgia"}
        >
          {dataResource.price ? `$${dataResource.price}` : "Free"}
        </Text>
        <Flex w={{ lg: "unset", base: "full" }}>
          <Button
            w="full"
            fontSize={"14px"}
            onClick={() =>
              dataResource.price
                ? dataResource.redirectURL
                  ? router.push(dataResource.redirectURL)
                  : notify(<>Something went wrong. Please try again later!</>, {
                      variant: "danger",
                    })
                : onOpen()
            }
            lineHeight={"14px"}
          >
            Get it now
          </Button>
        </Flex>
      </VStack>
      <PopUp
        isOpen={isOpen}
        onClose={onClose}
        downloadLink={dataResource.redirectURL?.toString()}
        topic={{ category: dataResource.category, tag: "Free", title: dataResource.title }}
      />
    </Flex>
  );
};
const Resource = ({ data }: ResourceProps) => {
  return (
    <Flex
      direction={"column"}
      px={{ xl: "unset", lg: "24px", base: "16px" }}
      py={{ xl: "100px", lg: "80x", base: "64px" }}
      maxWidth={"1440px"}
      mx="auto"
    >
      <Flex direction="column" align={"start"} w="full" mb={{ lg: "64px", base: "40px" }}>
        <Text
          fontSize={{ lg: "18px", base: "14px" }}
          color="rgba(13, 48, 80, 0.8)"
          lineHeight={{ lg: "18px", base: "14px" }}
          mb={{ lg: "16px", base: "12px" }}
          textAlign={"center"}
          w="full"
        >
          RESOURCES
        </Text>
        <Text
          fontSize={{ lg: "56px", base: "32px" }}
          color="#0D3050"
          lineHeight={{ lg: "64px", base: "36px" }}
          fontFamily={"Georgia"}
          mb={{ lg: "24px", base: "16px" }}
          textAlign={"center"}
          w="full"
        >
          Improve Your Performance with <LargeBreak /> <MediumBreak /> Our Coaching Resources
        </Text>
        <Text
          fontSize={{ lg: "24px", base: "16px" }}
          color="rgba(13, 48, 80, 0.8)"
          lineHeight={{ lg: "32px", base: "24px" }}
          textAlign={"center"}
          letterSpacing={{ base: "0.02em" }}
          w="full"
        >
          Our coaching resources are designed to provide you with the support you need{" "}
          <LargeBreak />
          <MediumBreak /> <SmallBreak /> to overcome obstacles and reach <SmallBreak /> your full
          potential.
        </Text>
      </Flex>

      <Flex direction={"column"} gap={{ lg: "40px", base: "64px" }}>
        {data.resources?.map((dataResource, idx) => {
          return (
            <Flex key={idx}>
              <Card dataResource={dataResource} />
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};

export default Resource;
